import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
    ExcelPassportHeader,
    ExcelPassportID,
    ExcelPassport,
    ExcelLoadState,
    ExcelEmbedUrlRequest,
    ExcelPassportDefault,
    ExcelPassportFormState,
    ExcelPassportAuthCode,
    ExcelCodeChallenge,
    ExcelSheetNamesRequest,
    ExcelSheetNamesResponse,
} from "userful-chronos-app-common-js/dist/models/integration/excel";
import {
    requestListPassports,
    requestAddPassport,
    requestUpdatePassport,
    requestRemovePassport,
    requestUpdatePassportWithOAuth,
    requestBaseURL,
    requestPreviewURL,
    requestCodeChallenge,
    requestAccessToken,
    requestSheetNamesFromServer,
} from "./msgs/excelMsgSender";

const initialState: {
    excelPassports: ExcelPassport[];
    loadState: ExcelLoadState;
    passport: ExcelPassport;
    copyPassport: ExcelPassport;
    formType: ExcelPassportFormState;
    openForm: boolean;
    authCode: string;
    codeChallenge: string;
    accessTokenGraph: string;
    accessTokenExcel: string;
    baseURL: string;
    sheetNames: string[];
    sheetNamesLoadState: "IDLE" | "LOADING" | "SUCCESS" | "ERROR";
} = {
    excelPassports: [],
    loadState: "RENDER",
    passport: ExcelPassportDefault(),
    copyPassport: ExcelPassportDefault(),
    formType: "Create",
    openForm: false,
    authCode: "",
    codeChallenge: "",
    accessTokenGraph: "",
    accessTokenExcel: "",
    baseURL: "",
    sheetNames: [],
    sheetNamesLoadState: "IDLE",
};

export const excelSlice = createSlice({
    name: "excelSlice",
    initialState,
    reducers: {
        setPassports: (state, action: PayloadAction<ExcelPassport[]>) => {
            state.excelPassports = action.payload;
        },
        addOrUpdatePassport: (state, action: PayloadAction<ExcelPassport>) => {
            const foundIndex = state.excelPassports.findIndex((item) => item.id.value === action.payload.id.value);
            if (foundIndex < 0) {
                state.excelPassports = [...state.excelPassports, { ...action.payload }];
            } else {
                state.excelPassports = [
                    ...state.excelPassports.slice(0, foundIndex),
                    { ...action.payload },
                    ...state.excelPassports.slice(foundIndex + 1),
                ];
            }
        },
        removePassport: (state, action: PayloadAction<ExcelPassportID>) => {
            state.excelPassports = state.excelPassports.filter((item) => item.id.value !== action.payload.value);
        },
        getPassportsFromServer: (state, action: PayloadAction<{}>) => {
            requestListPassports();
        },
        addPassportToServer: (state, action: PayloadAction<ExcelPassportHeader>) => {
            requestAddPassport(action.payload);
        },
        updatePassportToServerWithOAuth: (state, action: PayloadAction<ExcelPassportHeader>) => {
            requestUpdatePassportWithOAuth(action.payload);
        },
        updatePassportToServer: (state, action: PayloadAction<ExcelPassportHeader>) => {
            requestUpdatePassport(action.payload);
        },
        removePassportToServer: (state, action: PayloadAction<ExcelPassportID>) => {
            requestRemovePassport(action.payload);
        },
        changeLoadState: (state, action: PayloadAction<ExcelLoadState>) => {
            state.loadState = action.payload;
        },
        setPassport: (state, action: PayloadAction<ExcelPassport>) => {
            state.passport = action.payload;
        },
        setCopyPassport: (state, action: PayloadAction<ExcelPassport>) => {
            state.copyPassport = action.payload;
        },
        setFormType: (state, action: PayloadAction<ExcelPassportFormState>) => {
            state.formType = action.payload;
        },
        setOpenForm: (state, action: PayloadAction<boolean>) => {
            state.openForm = action.payload;
        },
        setAuthCode: (state, action: PayloadAction<string>) => {
            state.authCode = action.payload;
        },
        setCodeChallenge: (state, action: PayloadAction<string>) => {
            state.codeChallenge = action.payload;
        },
        requestCodeChallenge: (state, action: PayloadAction<ExcelPassportID>) => {
            requestCodeChallenge(action.payload);
        },
        requestBaseURL: (state, action: PayloadAction<ExcelPassportID>) => {
            requestBaseURL(action.payload);
        },
        requestAccessToken: (state, action: PayloadAction<ExcelPassportID>) => {
            requestAccessToken(action.payload);
        },
        setAccessTokenGraph: (state, action: PayloadAction<string>) => {
            state.accessTokenGraph = action.payload;
        },
        setAccessTokenExcel: (state, action: PayloadAction<string>) => {
            state.accessTokenExcel = action.payload;
        },
        setBaseURL: (state, action: PayloadAction<string>) => {
            state.baseURL = action.payload;
        },
        requestSheetNames: (state, action: PayloadAction<ExcelSheetNamesRequest>) => {
            state.sheetNamesLoadState = "LOADING";
            state.sheetNames = [];
            requestSheetNamesFromServer(action.payload);
        },
        setSheetNames: (state, action: PayloadAction<string[]>) => {
            state.sheetNames = action.payload;
            state.sheetNamesLoadState = "SUCCESS";
        },
        setSheetNamesLoadState: (state, action: PayloadAction<"IDLE" | "LOADING" | "SUCCESS" | "ERROR">) => {
            state.sheetNamesLoadState = action.payload;
            if (action.payload === "ERROR" || action.payload === "IDLE") {
                state.sheetNames = [];
            }
        },
        clearSheetNames: (state) => {
            state.sheetNames = [];
            state.sheetNamesLoadState = "IDLE";
        },
    },
});

export const excelActions = excelSlice.actions;

export default excelSlice.reducer;
